/**
 * WHAT IS BEHIND ONE DOOR.
 *
 * Canon 1.3: "the card is a door, the permission is what determines what's
 * actually behind it once opened."
 *
 * Read literally, that is a build instruction: a card's SECTIONS are its
 * permissions. So this page has no per-card section list, invents no heading
 * the database cannot justify, and cannot drift from admin_card_permissions --
 * it renders whatever fn_my_card_permissions returns, turned into sections by
 * lib/sections.js.
 *
 * WHAT THIS REPLACED
 *
 * A flat list under "What your account may do here". Accurate, and a settings
 * page: twelve of the fourteen cards opened to nothing else, so a finance
 * officer opening Finance was told what they were permitted to do and given no
 * way to do any of it.
 *
 * A SECTION WITH NOTHING BEHIND IT SAYS SO
 *
 * Named, described in the permission's own words, and marked unbuilt. Not
 * hidden: a card that silently omits what it cannot yet do makes the gap
 * invisible to whoever has to close it. And not an empty table, which reads as
 * a working module with no work in it -- a worse lie than an honest gap.
 *
 * The safeguards come from the database, not from a constant here, for the same
 * reason the cards do: requires_reason_capture is a property of the permission,
 * and a screen that remembers it separately is a screen that forgets it
 * separately.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { O, Card, StatusPill, LoadingPanel, Notice, QuietButton, TrustFooter } from './ui';
import { fetchCardPermissions } from './lib/portal';
import { sectionsFor, workingCount } from './lib/sections';
import { Page } from './Portal';
import ReasonGate from './ReasonGate';
import ProviderApplications from './modules/ProviderApplications';
import ProviderVerification from './modules/ProviderVerification';
import SanctionsReview from './modules/SanctionsReview';
import IdentityReattach from './modules/IdentityReattach';
import OwnershipQueue from './modules/OwnershipQueue';
import LandlordBusinessQueue from './modules/LandlordBusinessQueue';
import ReportedPhotoQueue from './modules/ReportedPhotoQueue';
import AgentProfileInterest from './modules/AgentProfileInterest';
import DisputeQueue from './modules/DisputeQueue';
import StaffDirectory from './modules/StaffDirectory';
import CustomerEnquiries from './modules/CustomerEnquiries';
import SupportTicketDesk from './modules/SupportTicketDesk';
import FieldOnboarding from './modules/FieldOnboarding';
import DebitOrderMandates from './modules/DebitOrderMandates';
import TierChanges from './modules/TierChanges';
import SubjectLookup from './modules/SubjectLookup';
import OrchidInsights from './modules/OrchidInsights';
import InternalAudit from './modules/InternalAudit';

/**
 * The screens, by the SURFACE name lib/sections.js gives them.
 *
 * Keyed by surface rather than by card, because a screen belongs to a
 * capability and not to a door: Compliance's two queues are two sections
 * because view_kyc_queue and approve_verification are two permissions, and if
 * one of them were ever moved to another card the screen would move with it
 * without a line changing here.
 */
const SURFACE_SCREENS = {
  // An application is somebody asking to exist on Orchid at all.
  provider_applications: ProviderApplications,
  // Verification is what a business that already exists clears before a client
  // is told it is verified -- the facts the badge is derived from, each waiting
  // on somebody to look at a document.
  //
  //   "how does a provider get verified? if it's something admin has to do,
  //    write the path for it in the Orchid Portal."
  provider_verification: ProviderVerification,
  // FIC Act s26A. The screening has run on every new profile since script 29;
  // until script 205 registered manage_sanctions_list, every desk but the
  // single 'all' holder read an empty queue and could not tell that from a
  // clean one.
  sanctions_review: SanctionsReview,
  // Script 268. An identity that attached to the wrong one of somebody's own
  // accounts, moved to the one it belongs to -- under its own permission, with
  // its own reason, because reading a footprint and changing one are two acts.
  identity_reattach: IdentityReattach,
  // Script 276. Every unit must be proven owned before it can operate, and
  // until this screen the proving had no desk: a landlord's title deed went
  // into a pending row nothing listed, and a desk could only ever say yes.
  ownership_queue: OwnershipQueue,
  landlord_business_queue: LandlordBusinessQueue,
  // Script 344. A listing photograph goes straight through and is reportable,
  // and a report hides it PENDING REVIEW. This is what "pending" means: without
  // it a report is a permanent deletion any signed-in person can perform on any
  // landlord's photograph.
  reported_photo_queue: ReportedPhotoQueue,
  // Script 345. "landlord profiles can only add units they own" -- and everybody
  // who chose "I manage it" instead is counted here, because the founder asked
  // for the number that decides whether an Agency profile is worth building.
  agent_profile_interest: AgentProfileInterest,
  // 241 gave a tenant a way to file a dispute and 242 gave a desk a way to
  // settle one. Between the two, a filed dispute carried both parties'
  // testimony and nobody at Orchid could see it.
  dispute_queue: DisputeQueue,
  // The flags this card is named for, and the POPIA retention clock. Both are
  // gated on view_admin_flags; the flags need a reason and the clock does not,
  // so the clock renders first and the queue is opened deliberately.
  internal_audit: InternalAudit,
  staff_directory: StaffDirectory,
  // Everything submitted through Get in Touch on the marketing site arrives
  // here -- before script 61 it went nowhere at all, because the form set a
  // flag and discarded the message.
  customer_enquiries: CustomerEnquiries,
  // NOT REACHED ANY MORE, AND KEPT ON PURPOSE.
  //
  //   "let it stand by itself outside all the cards, at the very top of the
  //    screen. That's going to be our focus for the next few months so it must
  //    be easily findable."
  //
  // field_onboarding is marked standsAlone in lib/sections, so sectionsFor
  // never hands this map that surface and Portal.jsx draws the screen itself.
  // The entry stays because the mapping is still true -- that surface IS this
  // component -- and deleting it would mean a future decision to put the stand
  // back behind a door needed the wiring rebuilt rather than one flag removed.
  field_onboarding: FieldOnboarding,
  // The record FNB's collections vetting asks for: the mandate a provider or
  // landlord gave, and the file the bank loads. The only screen on the platform
  // behind which anything is decrypted.
  debit_order_mandates: DebitOrderMandates,
  // Where a customer first asks to change what they pay. Before script 138 an
  // Enterprise enquiry was raised correctly and read by nothing: the portal
  // had never called support_tickets at all.
  tier_changes: TierChanges,
  // Everything raised from inside the apps. Fourteen ticket functions existed
  // and one was called by any JavaScript anywhere, so a ticket raised outside a
  // tier change arrived correctly and was seen by nobody -- on either side, as
  // the member could not raise one at all.
  support_tickets: SupportTicketDesk,

  // LOOKING ONE SUBJECT UP, FROM FOUR DESKS.
  //
  //   "EVERY BIT OF DATA READ AND WRITTEN, ON EVERY PROFILE'S SCREEN, must be
  //    viewable by staff with the necessary permissions."
  //
  // One component, four lenses. They are four surfaces rather than one screen
  // with four tabs because they sit on four different cards: a Finance officer
  // opening Finance should get the money, not a screen with three sections
  // greyed out. The lens decides what is OFFERED; the database decides what is
  // ALLOWED, and it decides it again on every read regardless of what is drawn.
  subject_summary:   () => <SubjectLookup lens="subject_summary" />,
  subject_finance:   () => <SubjectLookup lens="subject_finance" />,
  subject_trust:     () => <SubjectLookup lens="subject_trust" />,
  subject_documents: () => <SubjectLookup lens="subject_documents" />,
  job_conversation:  () => <SubjectLookup lens="job_conversation" />,

  // The card, and the file that leaves the building.
  orchid_insights: OrchidInsights,
};

/** What a shared capability acts inside, in the words on the heading above it. */
const SURFACE_NAMES = {
  provider_applications: 'Applications',
  provider_verification: 'Verification queue',
  sanctions_review: 'Sanctions screening',
  dispute_queue: 'Payment disputes',
  internal_audit: 'Fraud and anomaly flags',
  staff_directory: 'Staff directory',
  customer_enquiries: 'Enquiries',
  field_onboarding: 'Field onboarding',
  debit_order_mandates: 'Debit order mandates',
  subject_summary: 'Look somebody up',
  subject_finance: 'A subject’s money',
  subject_trust: 'A subject’s standing',
  subject_documents: 'Identity documents',
  ownership_queue: 'Ownership proof',
  job_conversation: 'Read a job’s conversation',
  orchid_insights: 'Orchid Insights',
};

export default function CardScreen({ cards }) {
  const { slug } = useParams();
  const card = (cards || []).find((c) => c.slug === slug);

  const [permissions, setPermissions] = useState(null);
  const [error, setError] = useState(null);
  const [gate, setGate] = useState(null);

  useEffect(() => {
    let alive = true;
    setPermissions(null);
    setError(null);
    fetchCardPermissions(slug).then((res) => {
      if (!alive) return;
      if (res.error) setError(res.error);
      else setPermissions(res.data);
    });
    return () => { alive = false; };
  }, [slug]);

  // A card not in the menu is a card this person cannot open. The message says
  // that, rather than "not found", which would be a small lie: it exists.
  if (!card) {
    return (
      <Page>
        <Link to="/" style={{ fontSize: 12.5, color: O.green, textDecoration: 'none' }}>← Portal</Link>
        <div style={{ height: 22 }} />
        <Card>
          <h1 style={{ fontSize: 16, fontWeight: 700, color: O.ink }}>
            This module is not open to your account.
          </h1>
          <p style={{ marginTop: 7, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
            Cards appear on your portal when you hold at least one permission inside them.
          </p>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <Link to="/" style={{ fontSize: 12.5, color: O.green, textDecoration: 'none' }}>← Portal</Link>

      <header style={{ margin: '22px 0 22px' }}>
        <h1 style={{ fontSize: 21, fontWeight: 700, color: O.ink }}>{card.title}</h1>
        <p style={{ marginTop: 6, fontSize: 13, lineHeight: 1.6, color: O.inkMuted }}>
          {card.description}
        </p>
      </header>

      {error && <Notice tone="critical">{error}</Notice>}
      {permissions === null && !error && <LoadingPanel message="Opening this module…" />}

      {/* THE DOCUMENT SHELF THAT STOOD HERE IS GONE.
          "and then obviously remove the reference to these documents that
           appears at the top of each card's screen."
          Every one of them — this card's SOP, its welcome pack and the company
          pack — is on Resources now, grouped by department, alongside the HR
          resources and processes that could not be stored at all before script
          330. Nothing was dropped; one shelf replaced fourteen. */}
      {permissions && permissions.length === 0 && !error && (
        <Card><p style={{ fontSize: 13, color: O.inkMuted }}>
          You hold no permissions inside this module.
        </p></Card>
      )}

      {permissions && permissions.length > 0 && (() => {
        const sections = sectionsFor(permissions);
        const working = workingCount(sections);

        return (
          <>
            {/* Said once, at the top, rather than as a footnote nobody reaches
                after scrolling past nine unbuilt sections. */}
            <p style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: O.inkFaint, marginBottom: 6,
            }}>
              {sections.length} section{sections.length === 1 ? '' : 's'}
            </p>
            <p style={{ fontSize: 12, lineHeight: 1.6, color: O.inkMuted, marginBottom: 18 }}>
              {/* WORKING, not built-on-this-card. A capability that stands on
                  the home screen belongs in neither half of "these work / the
                  rest are named rather than hidden", and counting it in the
                  second would tell somebody a screen they use every day does
                  not exist. */}
              {working === sections.length
                ? 'Every section here is working.'
                : working === 0
                  ? 'None of these has a screen behind it yet. They are named rather than hidden, '
                    + 'so what is missing is visible to whoever has to build it.'
                  : `${working} of them ${working === 1 ? 'has a screen' : 'have screens'} behind `
                    + 'them today. The rest are named rather than hidden.'}
            </p>

            <div style={{ display: 'grid', gap: 12 }}>
              {sections.map((section) => {
                const Screen = section.surface ? SURFACE_SCREENS[section.surface] : null;
                return (
                  <Card key={section.key}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                      {/* "name the sections without underscores i.e. not
                          approve_verification, but Approve Verification." The
                          KEY is unchanged -- it is still what
                          admin_has_permission() is asked and what the audit log
                          records. This is how it is read. */}
                      <h3 style={{ fontSize: 14.5, fontWeight: 700, color: O.ink, margin: 0 }}>
                        {section.title}
                      </h3>
                      {section.requiresMfa && <StatusPill tone="quiet">MFA</StatusPill>}
                      {section.requiresReason && <StatusPill tone="warning">Reason required</StatusPill>}
                      {section.isExport && <StatusPill tone="critical">Export · time-boxed</StatusPill>}
                      {section.standsAlone && (
                        <StatusPill tone="green">On the home screen</StatusPill>
                      )}
                      {!section.built && !section.sharedWith && !section.standsAlone && (
                        <StatusPill tone="quiet">Not built yet</StatusPill>
                      )}
                    </div>

                    <p style={{ marginTop: 7, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
                      {section.blurb}
                    </p>

                    {/* A capability that acts inside a screen another section
                        already drew. Rendering the screen twice would make one
                        queue look like two. */}
                    {section.sharedWith && (
                      <p style={{ marginTop: 7, fontSize: 11.5, lineHeight: 1.6, color: O.inkFaint }}>
                        You use this inside {SURFACE_NAMES[section.sharedWith] || 'the section above'}.
                      </p>
                    )}

                    {/* THE PERMISSIONS THIS ACCOUNT ACTUALLY HOLDS ON THIS
                        CARD, handed down rather than re-asked. It is still the
                        database's answer -- fetchCardPermissions is what
                        produced it -- and a screen with two halves of different
                        weight needs to know which halves to draw.

                        Debit order mandates is the first such screen: reading a
                        mandate and DECRYPTING one for the bank are two
                        permissions, and Canon 4.3 refuses to grant the second
                        one directly at all, so holding the first without the
                        second is the ordinary case. Existing screens ignore the
                        prop. */}
                    {Screen && (
                      <div style={{ marginTop: 16 }}>
                        <Screen permissions={permissions} />
                      </div>
                    )}

                    {/* BUILT, IN USE, AND NOT HERE. Dropping the section would
                        read as a capability this account lost; marking it
                        unbuilt would be false. It says where it went and takes
                        you there. */}
                    {section.standsAlone && (
                      <p style={{ marginTop: 7, fontSize: 12, lineHeight: 1.7, color: O.inkMuted }}>
                        This stands on its own at the top of the{' '}
                        <Link to="/" style={{ color: O.green, fontWeight: 600 }}>Portal home screen</Link>,
                        above every card, because it is what Orchid is working on right now.
                      </p>
                    )}

                    {!section.built && !section.sharedWith && !section.standsAlone && (
                      <p style={{ marginTop: 7, fontSize: 11.5, lineHeight: 1.6, color: O.inkFaint }}>
                        Your account may do this. The screen for it has not been built, so it is
                        named here rather than left out — an empty table would read as a section
                        with no work in it, which is a different thing.
                      </p>
                    )}

                    {section.requiresReason && (
                      <div style={{ marginTop: 12, maxWidth: 280 }}>
                        <QuietButton onClick={() => setGate(
                          permissions.find((p) => p.permission_key === section.key),
                        )}>
                          Open a record under this permission
                        </QuietButton>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </>
        );
      })()}

      <TrustFooter />

      {gate && <ReasonGate permission={gate} onClose={() => setGate(null)} />}
    </Page>
  );
}
